import {
  Center,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  InputGroup,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Suspense, useCallback, useMemo } from 'react';
import { Trans, Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { CenteredSpinner } from '@duke-hq/app-admin-web/components/Loading';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { RegionsLocationsTable } from './RegionsLocationsTable';
import { RegionLocationsDetails } from './RegionLocationsDetails';
import { LocationError } from './LocationError';
import { constructURL } from '@duke-hq/lib';
import pickBy from 'lodash-es/pickBy.js';
import { emptyPaginationResponse, parseSortOrder, SortOrder } from '@duke-hq/lib/pagination';

export type SortFilters = {
  orderBy: string | undefined;
  sort: SortOrder | undefined;
  page: string | undefined;
};

import type { RegionsLocationsURLParams } from './types';

export function RegionsLocations() {
  const navigate = useNavigate();
  const { providerId, regionId } = useParams<RegionsLocationsURLParams>();
  const { pageSize, currentPage, setPage } = usePagination();

  const [searchParams, setSearchParams] = useSearchParams({
    orderBy: 'name',
    sort: SortOrder.ASC,
    page: '1',
  });

  const querySearch = searchParams.get('search') ?? '';

  const getURLFilters = useMemo(() => {
    const urlFilters = Object.fromEntries(searchParams.entries());
    const { page, search, orderBy, sort, ...rest } = urlFilters;
    return {
      ...rest,
      orderBy: orderBy ?? 'name',
      sort: parseSortOrder(sort) ?? SortOrder.ASC,
      page: page ?? '1',
    };
  }, [searchParams]);

  const handleFilterChange = useCallback(
    (newFilters: Partial<SortFilters>) => {
      const updatedFilters = pickBy(
        { ...Object.fromEntries(searchParams.entries()), ...newFilters },
        (value) => value != null && value != '',
      );

      if (updatedFilters.page && updatedFilters.page === '1') {
        delete updatedFilters.page;
      }

      const newSearchParams = new URLSearchParams(updatedFilters as Record<string, string>);
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const { isOpen } = useDisclosure({
    isOpen: !!regionId,
  });

  if (!providerId) {
    return <LocationError message={t`An error occurred, providerId is required.`} />;
  }

  const { data: regions, isFetching } =
    trpcAdmin.providers.locations.listSiteRegionsWithPagination.useQuery(
      {
        search: querySearch.trim(),
        filters: getURLFilters,
        providerId,
        pagination: {
          page: Number(currentPage),
          limit: pageSize,
        },
      },
      {
        enabled: Boolean(providerId),
        initialData: emptyPaginationResponse(),
        placeholderData: (previousData) => previousData,
      },
    );

  const onCloseHandler = () => {
    const url = constructURL(
      `/providers/${providerId}/locations`,
      Object.fromEntries(searchParams.entries()) as Record<string, string>,
    );
    navigate(url);
  };

  return (
    <>
      {regionId ? (
        <Drawer isOpen={isOpen} onClose={onCloseHandler} size="xl">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton onClick={onCloseHandler} />
            <Suspense fallback={<CenteredSpinner />}>
              <RegionLocationsDetails />
            </Suspense>
          </DrawerContent>
        </Drawer>
      ) : null}

      <TableViewLayout
        pageActions={[]}
        filters={
          <InputGroup size="sm">
            <SearchInput maxW="search-input" />
          </InputGroup>
        }
        tableActionsLeft={
          <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
            <Plural
              value={regions.pagination.totalCount || 0}
              zero="0 regions"
              one="1 region"
              other="# regions"
            />
          </Text>
        }
        table={
          <>
            <RegionsLocationsTable
              regions={regions.items}
              urlParams={getURLFilters}
              onFilters={handleFilterChange}
            />

            {isFetching ? (
              <Center p={8}>
                <Spinner size="xl" transitionDelay={'2900ms'} />
              </Center>
            ) : regions.items.length === 0 ? (
              <Center p={8}>
                <Text textAlign="center">
                  <Trans>No regions found</Trans>
                </Text>
              </Center>
            ) : null}
          </>
        }
        pagination={
          regions.items.length > 0 ? (
            <Pagination pagination={regions.pagination} onPageChange={setPage} />
          ) : null
        }
      />
    </>
  );
}
